import { TrackingWrapper } from '@js-boot/tracking-wrapper';
import type { DeviceAttributes } from './deviceAttributes';
import { getDeviceAttributes } from './deviceAttributes';
import type { TrackingEvent } from '../@types/trackingEvent';
import { KAEUFER_PLUS_PATH, MIETER_PLUS_PATH, WOHNEN_PLUS_PATH } from '../domain/routes';
import { APP_CONFIG } from '../config';
import { Environment } from '../@types/environment';

interface TealiumCall extends Partial<DeviceAttributes> {
  pag_pagetitle?: string;
  ga_cd_profile_status?: string;
  evt_ga_action?: string;
  evt_ga_label?: string;
  evt_ga_category?: string;
  event_name?: string;
  event_product?: string;
  event_parameter_1?: string;
  event_parameter_2?: string;
  event_parameter_3?: string;
  event_parameter_4?: string;
}

type TrackingFunction = (...args: unknown[]) => void;

export type CustomPageName = 'mieterplus' | 'wohnenplus' | 'kaeuferplus' | null;
interface TrackParams extends Omit<TrackingEvent, 'page'> {
  pageTitle: string;
  profileStatus?: string;
}

const getDynamicPageName = () => {
  return window.location.pathname
    .split('/')
    .find((path) => [KAEUFER_PLUS_PATH, MIETER_PLUS_PATH, WOHNEN_PLUS_PATH].includes(path));
};

const MEINPLUS_BENEFITS_PAGE_TITLE_PREFIX = 'is24.de.meinplus.vorteile';
const TENANT_DETAIL_PAGE_TITLE_PREFIX = 'is24.de.meinkonto.meine-immobilien.detail.tenant';
export const PROPERTY_HUB_PAGE_TITLE_PREFIX = 'is24.de.meinkonto.meine-immobilien';

// Simple lookup maps
const PROFILE_STATUS: Record<string, string> = { true: 'premium', false: 'basic' };
const PAGE_PREFIX = { MFE: TENANT_DETAIL_PAGE_TITLE_PREFIX, default: MEINPLUS_BENEFITS_PAGE_TITLE_PREFIX };

async function waitForTrackingFunction(): Promise<TrackingFunction> {
  const tealiumRetryInterval = 500;
  if (window.IS24?.TEALIUM?.tracking?.report) {
    return window.IS24.TEALIUM.tracking.report;
  }
  return new Promise((resolve) => {
    setTimeout(() => resolve(waitForTrackingFunction()), tealiumRetryInterval);
  });
}

export async function reportToTealium(data: TealiumCall, options = {}) {
  try {
    const trackingFunction = await waitForTrackingFunction();
    trackingFunction(data, options);
    if (APP_CONFIG.environment !== Environment.PRODUCTION) {
      window.console.log('Tealium tracking report:', data, options);
    }
  } catch (error) {
    if (APP_CONFIG.environment !== Environment.PRODUCTION) {
      window.console.warn('Tealium tracking report failed. Logging data and options:', { data, options, error });
    }
  }
}

const createPayload = ({
  pageTitle,
  action,
  label,
  name,
  product,
  parameter1,
  parameter2,
  parameter3,
  parameter4,
  profileStatus,
}: TrackParams): TealiumCall => ({
  ...getDeviceAttributes(typeof navigator === 'undefined' ? '' : navigator.userAgent),
  pag_pagetitle: pageTitle,
  ...(profileStatus && { ga_cd_profile_status: profileStatus }),
  ...(action && { evt_ga_action: action }),
  ...(label && { evt_ga_label: label, evt_ga_category: 'conmon' }),
  ...(name && { event_name: name }),
  ...(product && { event_product: product }),
  ...(parameter1 && { event_parameter_1: parameter1 }),
  ...(parameter2 && { event_parameter_2: parameter2 }),
  ...(parameter3 && { event_parameter_3: parameter3 }),
  ...(parameter4 && { event_parameter_4: parameter4 }),
});

export const report = async (
  event: TrackingEvent,
  isEntitled: boolean,
  customPageName: CustomPageName = null,
  pagePrefix?: string,
) => {
  const isTenantPage = window.location.pathname.includes('/meine-immobilien');
  const pageContext = isTenantPage ? 'MFE' : 'default';

  const profileStatus = PROFILE_STATUS[String(isEntitled)];
  const dynamicPageName = customPageName || getDynamicPageName();
  const defaultPrefix = PAGE_PREFIX[pageContext];

  const pageTitle = `${pagePrefix || defaultPrefix}${
    dynamicPageName ? '.' + dynamicPageName : ''
  }${event.page ? '.' + event.page : ''}`;

  const payload = createPayload({
    ...event,
    pageTitle,
    profileStatus,
  });

  const hasLabel = Boolean(event.label);
  const options = hasLabel ? undefined : { reportType: 'UTAG_VIEW' as const };
  const trackingMethods = {
    MFE: () => (options ? reportToTealium(payload, options) : reportToTealium(payload)),
    default: () => (options ? is24FrontendTracking.report(payload, options) : is24FrontendTracking.report(payload)),
  };

  await trackingMethods[pageContext]();
};

export const getTrackingConfiguration = (environment: Environment) => {
  const trackingBundleParameters = {
    counterName: 'is24.de.meinplus.vorteile',
    svcModuleName: 'vorteile_buy',
    initialPageView: false,
    reportEventsIndividually: true,
  };
  const logTrackingEventsInConsole = environment !== Environment.PRODUCTION;

  return {
    trackingBundleBaseUrl: APP_CONFIG.trackingBundleBaseUrl,
    trackingBundleParameters,
    logTrackingEventsInConsole,
    disableTracking: environment !== Environment.PRODUCTION && environment !== Environment.SANDBOX,
  };
};

export const is24FrontendTracking = new TrackingWrapper(getTrackingConfiguration(APP_CONFIG.environment));
